import React, {useCallback, useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import './styles';
import {useTranslation} from 'react-i18next';

import {IdCardIcon, LightbulbIcon} from '@/app/icons';
import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
} from '@/app/shared-components';

import {useViewModelProvider, ViewModelProvider} from './ViewModelContext';

const IdvContent = () => {
  const viewModel = useViewModelProvider();
  const openPlaid = viewModel.openPlaid;
  const isReady = viewModel.isReady;
  const plaidInitError = viewModel.plaidInitError;
  const retryInit = viewModel.retryInit;

  const {t} = useTranslation();

  const labelButton = useMemo(() => {
    if (plaidInitError) {
      return t('kyc.idv.retry');
    }
    return isReady ? t('kyc.idv.verification') : t('kyc.idv.waiting');
  }, [isReady, plaidInitError, t]);

  const isDisabled = useMemo(() => {
    if (plaidInitError) {
      return false; // Allow retry if there's an error
    }
    return !isReady; // Disable if not ready and no error
  }, [isReady, plaidInitError]);

  const handleOnClick = useCallback(() => {
    if (plaidInitError) {
      retryInit();
    } else {
      openPlaid();
    }
  }, [openPlaid, retryInit, plaidInitError]);

  return (
    <Box className="idv-page-container">
      {/* Titles Section */}
      <Box className="titles-container">
        <Typography
          variant="h3"
          className="principal-title-element">
          {t('kyc.idv.title')}
          <span className="secondary-title-element">
            {t('kyc.idv.title_2')}
          </span>
        </Typography>
        <Typography
          variant="h4"
          className="secondary-title-element">
          {t('kyc.idv.description')}
        </Typography>
      </Box>

      {/* Loader Section */}
      <Box className="loader-container">
        <Box
          component="img"
          src="/assets/images/logo/modak-circle-logo.svg"
          alt="Modak Logo"
          className="loader-logo"
        />
        <Box
          component="img"
          src="/assets/images/pages/LightArrow.webp"
          alt="Light arrow"
          className="mdk-lottie"
        />
        <Box
          component="img"
          src="/assets/images/logo/plaid_circle_logo.svg"
          alt="Plaid Logo"
          className="loader-logo"
        />
      </Box>

      {/* Recommendations Section */}
      <Box className="recommendations-container">
        <Box className="recommendations-box-container">
          <Box className="recommendation-item">
            <IdCardIcon />
            <Typography className="recommendations-item-text">
              {t('kyc.idv.id_ready')}
            </Typography>
          </Box>

          <Box className="recommendation-item">
            <LightbulbIcon />
            <Typography className="recommendations-item-text">
              {t('kyc.idv.selfie')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Verification Button Section */}
      <Box className="button-verification-container">
        <ButtonContainer sticky>
          <Typography className="idv-disclaimer-text">
            {t('kyc.idv.disclaimer')}
          </Typography>
          <AppButton
            id="idv-go-to-verification-button"
            variant={AppButtonVariant.onPrimary}
            label={labelButton}
            disabled={isDisabled}
            onClick={handleOnClick}
          />
        </ButtonContainer>
      </Box>
    </Box>
  );
};

const IdvPage = () => (
  <ViewModelProvider>
    <IdvContent />
  </ViewModelProvider>
);

export default IdvPage;
